var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from) {
    for (var i = 0, il = from.length, j = to.length; i < il; i++, j++)
        to[j] = from[i];
    return to;
};
import { jsx as _jsx } from "react/jsx-runtime";
import { useContext, useMemo, useRef, useState } from 'react';
import Select from 'react-select';
import styled, { ThemeContext } from 'styled-components';
import useFocus from '../../../hooks/useFocus';
import { ThemeFamilies } from '../../../theme/families';
import FieldBox, { FieldBoxVariant } from '../FieldBox';
import { getCustomComponents, getCustomStyles } from './customElements';
import FormatOptionLabelWithFlag from './customElements/FormatOptionLabelWithFlag';
import { OptionWrapper, StyledCheckBox } from './customElements/OptionWithCheckbox';
import { FontWeights } from '../../../theme/typography';
var Dropdown = function (_a) {
    var className = _a.className, name = _a.name, value = _a.value, placeholder = _a.placeholder, label = _a.label, themeFamily = _a.themeFamily, fieldBoxProps = _a.fieldBoxProps, customComponents = _a.customComponents, isMulti = _a.isMulti, disabled = _a.disabled, isVirtualized = _a.isVirtualized, isGroup = _a.isGroup, isOptionWithInfo = _a.isOptionWithInfo, isFocused = _a.isFocused, subMenus = _a.subMenus, popoverContent = _a.popoverContent, menuPortalTarget = _a.menuPortalTarget, isFlag = _a.isFlag, customOptionContent = _a.customOptionContent, isSearchable = _a.isSearchable, selectAllLabel = _a.selectAllLabel, selectedLabel = _a.selectedLabel, _b = _a.onChange, onChange = _b === void 0 ? function (_) { } : _b, selectableGroup = _a.selectableGroup, options = _a.options, selectProps = __rest(_a, ["className", "name", "value", "placeholder", "label", "themeFamily", "fieldBoxProps", "customComponents", "isMulti", "disabled", "isVirtualized", "isGroup", "isOptionWithInfo", "isFocused", "subMenus", "popoverContent", "menuPortalTarget", "isFlag", "customOptionContent", "isSearchable", "selectAllLabel", "selectedLabel", "onChange", "selectableGroup", "options"]);
    var _c = useState(false), isMenuOpen = _c[0], setMenuOpen = _c[1];
    var thisDropdown = useRef();
    var fieldBoxRef = useRef();
    var theme = useContext(ThemeContext);
    var formatedOptions = useMemo(function () {
        if (!isMulti || !selectableGroup)
            return options;
        return options.map(function (option) {
            if (!option.options)
                return option;
            var currentValue = Array.isArray(value) ? value : [];
            var foundInValue = function (option) { return !!currentValue.find(function (v) { return v.value === option.value; }); };
            var checked = option.options.every(foundInValue);
            var indeterminate = option.options.some(foundInValue);
            var handleClick = function () {
                var nextValue;
                if (checked || indeterminate) {
                    nextValue = currentValue.filter(function (valueObj) { return !option.options.find(function (op) { return op.value === valueObj.value; }); });
                }
                else {
                    nextValue = __spreadArray(__spreadArray([], currentValue), option.options);
                }
                // @ts-ignore
                onChange(nextValue);
            };
            return __assign(__assign({}, option), { value: option.label, label: (_jsx(OptionWrapper, __assign({ "data-testid": value }, { children: _jsx(StyledCheckBox, __assign({ checked: checked, onChange: handleClick, disabled: option.isDisabled, indeterminate: indeterminate }, { children: _jsx(BoldGroupLabel, { children: option.label }, void 0) }), void 0) }), void 0)) });
        });
    }, [options, value]);
    var overrideSelectProps = __assign(__assign(__assign({}, selectProps), (isFlag && { formatOptionLabel: FormatOptionLabelWithFlag })), { options: formatedOptions, themeFamily: themeFamily });
    var customComponentProps = {
        isMulti: isMulti,
        isVirtualized: isVirtualized,
        isGroup: isGroup,
        isOptionWithInfo: isOptionWithInfo,
        subMenus: subMenus,
        popoverContent: popoverContent,
        customOptionContent: customOptionContent,
        themeFamily: themeFamily,
    };
    var multiOptions = isMulti
        ? {
            isMulti: true,
            hideSelectedOptions: false,
            closeMenuOnSelect: false,
            backspaceRemovesValue: false,
            selectAllLabel: selectAllLabel,
            selectedLabel: selectedLabel,
        }
        : {};
    useFocus({ isFocused: isFocused, ref: thisDropdown });
    var handleMenuClose = function () {
        setMenuOpen(false);
        if (selectProps.onMenuClose) {
            selectProps.onMenuClose();
        }
    };
    var handleMenuOpen = function () {
        setMenuOpen(true);
        if (selectProps.onMenuOpen) {
            selectProps.onMenuOpen();
        }
    };
    var handleKeyDown = function (e) {
        if (e.key === 'Escape' && isMenuOpen) {
            e.stopPropagation();
        }
        if (selectProps.onKeyDown) {
            selectProps.onKeyDown(e);
        }
    };
    return (_jsx(StyledFieldBox, __assign({ name: name, themeFamily: themeFamily, className: className, label: label, value: value, disabled: disabled, onClear: function () { return onChange('', null); }, ref: fieldBoxRef }, fieldBoxProps, { children: _jsx(StyledSelect, __assign({ classNamePrefix: "dropdown", name: name, ref: thisDropdown, openMenuOnFocus: true, isClearable: false, isSearchable: isSearchable, value: value ? value : null, styles: getCustomStyles(theme, themeFamily, subMenus, menuPortalTarget, fieldBoxRef), placeholder: (!label || (fieldBoxProps === null || fieldBoxProps === void 0 ? void 0 : fieldBoxProps.variant) === FieldBoxVariant.Outer) && placeholder }, getCustomComponents(customComponentProps), overrideSelectProps, multiOptions, { isDisabled: disabled, subMenus: subMenus, popoverContent: popoverContent, menuPortalTarget: menuPortalTarget, blurInputOnSelect: false, onKeyDown: handleKeyDown, onMenuClose: handleMenuClose, onMenuOpen: handleMenuOpen, onChange: onChange }), void 0) }), void 0));
};
Dropdown.defaultProps = {
    themeFamily: ThemeFamilies.PRIMARY,
    isSearchable: true,
};
export default Dropdown;
var StyledFieldBox = styled(FieldBox)(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  position: relative;\n"], ["\n  position: relative;\n"])));
var StyledSelect = styled(Select)(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n  padding: 0;\n\n  .icon-chevron_down {\n    transition: 0.2s;\n  }\n\n  .dropdown__control--menu-is-open {\n    .icon-chevron_down {\n      transform: rotate(180deg);\n    }\n  }\n"], ["\n  padding: 0;\n\n  .icon-chevron_down {\n    transition: 0.2s;\n  }\n\n  .dropdown__control--menu-is-open {\n    .icon-chevron_down {\n      transform: rotate(180deg);\n    }\n  }\n"])));
var BoldGroupLabel = styled.span(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n  font-weight: ", ";\n  color: ", ";\n"], ["\n  font-weight: ", ";\n  color: ", ";\n"])), FontWeights.SEMI_BOLD, function (_a) {
    var theme = _a.theme;
    return theme.colors.disabled;
});
var templateObject_1, templateObject_2, templateObject_3;
